import type { RouteObject } from "react-router";

/**
 * The Student Attendance workspace's routes (M20, contracts §11), in a file of their own like
 * `staffRoutes.tsx` — `index.tsx` mounts this under the shell in one line.
 *
 * One layout route for the whole workspace: `StudentsWorkspaceGate` answers the add-on question once (the
 * entitlements, or any students read refused with `402 ADDON_REQUIRED`) and shows `AddonGate` in the
 * page's place. Every page is a file in its owning lane's folder (`src/modules/students/<area>/pages/`); a
 * lane replaces the file and never edits this table. Static segments come before the parameter beside them
 * (`students/new` before `students/:personId`).
 */
export const studentsRoute: RouteObject = {
  path: "students",
  lazy: async () => ({
    Component: (await import("@/modules/students/shared/StudentsWorkspaceGate")).StudentsWorkspaceGate,
  }),
  children: [
    {
      index: true,
      handle: { title: "Today" },
      lazy: async () => ({
        Component: (await import("@/modules/students/today/pages/StudentsTodayPage")).StudentsTodayPage,
      }),
    },
    {
      path: "students",
      handle: { title: "Students" },
      lazy: async () => ({
        Component: (await import("@/modules/students/students/pages/StudentsPage")).StudentsPage,
      }),
    },
    {
      path: "students/new",
      handle: { title: "Register student" },
      lazy: async () => ({
        Component: (await import("@/modules/students/students/pages/RegisterStudentPage"))
          .RegisterStudentPage,
      }),
    },
    {
      path: "students/import",
      handle: { title: "Import students" },
      lazy: async () => ({
        Component: (await import("@/modules/students/students/pages/ImportStudentsPage")).ImportStudentsPage,
      }),
    },
    {
      // Keyed on the shared `people.id`: a student is a person (ADR-0025); the core person page's
      // "Also in: Students" link lands here (`personClaims`).
      path: "students/:personId",
      handle: { title: "Student" },
      lazy: async () => ({
        Component: (await import("@/modules/students/students/pages/StudentPage")).StudentPage,
      }),
    },
    {
      // The day drawer is `?person&day` on this page, not a route of its own.
      path: "attendance",
      handle: { title: "Attendance" },
      lazy: async () => ({
        Component: (await import("@/modules/students/attendance/pages/StudentsAttendancePage"))
          .StudentsAttendancePage,
      }),
    },
    {
      path: "groups",
      handle: { title: "Groups" },
      lazy: async () => ({
        Component: (await import("@/modules/students/groups/pages/StudentGroupsPage")).StudentGroupsPage,
      }),
    },
    {
      path: "groups/:groupId",
      handle: { title: "Group" },
      lazy: async () => ({
        Component: (await import("@/modules/students/groups/pages/StudentGroupPage")).StudentGroupPage,
      }),
    },
    {
      path: "timetables",
      handle: { title: "Timetables" },
      lazy: async () => ({
        Component: (await import("@/modules/students/calendar/pages/TimetablesPage")).TimetablesPage,
      }),
    },
    {
      path: "calendar",
      handle: { title: "Calendar" },
      lazy: async () => ({
        Component: (await import("@/modules/students/calendar/pages/CalendarPage")).CalendarPage,
      }),
    },
    {
      path: "exceptions",
      handle: { title: "Exceptions" },
      lazy: async () => ({
        Component: (await import("@/modules/students/calendar/pages/ExceptionsPage")).ExceptionsPage,
      }),
    },
    {
      path: "promotions",
      handle: { title: "Promotions" },
      lazy: async () => ({
        Component: (await import("@/modules/students/promotions/pages/PromotionsPage")).PromotionsPage,
      }),
    },
    {
      // The module's own reports hub — never a card on the core `/reports` one (ADR-0017 addendum).
      path: "reports",
      handle: { title: "Student reports" },
      lazy: async () => ({
        Component: (await import("@/modules/students/reports/pages/StudentsReportsPage")).StudentsReportsPage,
      }),
    },
    {
      path: "reports/:kind",
      handle: { title: "Student report" },
      lazy: async () => ({
        Component: (await import("@/modules/students/reports/pages/StudentsReportPage")).StudentsReportPage,
      }),
    },
    {
      path: "settings",
      handle: { title: "Student settings" },
      lazy: async () => ({
        Component: (await import("@/modules/students/settings/pages/StudentSettingsPage"))
          .StudentSettingsPage,
      }),
    },
    {
      // C4: the SMS sent to guardians, from the students side (`?group&person&day&status`). No nav entry.
      path: "sms",
      handle: { title: "SMS to parents" },
      lazy: async () => ({
        Component: (await import("@/modules/students/sms/pages/StudentSmsMessagesPage"))
          .StudentSmsMessagesPage,
      }),
    },
    {
      // The setup guide (lane G): `?step=<id>`, `?person=<id>`. The breadcrumb's title is "Setup guide".
      path: "setup",
      handle: { title: "Setup guide" },
      lazy: async () => ({
        Component: (await import("@/modules/students/setup/pages/StudentsSetupPage")).StudentsSetupPage,
      }),
    },
  ],
};
